import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  getLeadPaymentLinks,
  cancelCashfreePaymentLink,
  verifyCashfreePaymentLink,
} from 'services/paymentServices';

export const usePaymentLinks = (leadId, refreshProgressJourney) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [transactionSyncing, setTransactionSyncing] = useState({});

  const loadPaymentLinks = useCallback(async (integrationProductId = null) => {
    setLoading(true);
    try {
      const response = await getLeadPaymentLinks(leadId, integrationProductId);
      if (response.success) {
        setTransactions(response.data || []);
        await refreshProgressJourney();
      } else {
        toast.error(response.error || 'Failed to load payment links');
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error loading payment links:', error);
      toast.error('Failed to load payment links');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  const handleSyncAll = useCallback(async (integrationProductId = null) => {
    setSyncing(true);
    try {
      const response = await getLeadPaymentLinks(leadId, integrationProductId);
      if (response.success) {
        setTransactions(response.data || []);
        toast.success('Payment links synced successfully');
        await refreshProgressJourney();
      } else {
        toast.error(response.error || 'Failed to sync payment links');
      }
    } catch (error) {
      console.error('Error syncing payment links:', error);
      toast.error('Failed to sync payment links');
    } finally {
      setSyncing(false);
    }
  }, [leadId]);

  const handleSyncTransaction = useCallback(async (transaction) => {
    if (!transaction?.LinkId) {
      toast.error('LinkId is required to sync payment status');
      return;
    }

    setTransactionSyncing(prev => ({ ...prev, [transaction.LinkId]: true }));

    try {
      const response = await verifyCashfreePaymentLink(transaction.LinkId);

      if (response.success) {
        const updatedData = response.data || {};
        await refreshProgressJourney();

        setTransactions(prev =>
          prev.map(txn =>
            txn.PaymentLinkId === transaction.PaymentLinkId
              ? { ...txn, ...updatedData }
              : txn
          )
        );

        toast.success(response.message || 'Payment status synced');
      } else {
        toast.error(response.error || 'Failed to sync payment status');
      }
    } catch (error) {
      console.error('Error syncing payment status:', error);
      toast.error('Failed to sync payment status');
    } finally {
      setTransactionSyncing(prev => {
        const updated = { ...prev };
        delete updated[transaction.LinkId];
        return updated;
      });
    }
  }, [refreshProgressJourney]);

  const handleCancelLink = useCallback(async (transaction) => {
    try {
      const response = await cancelCashfreePaymentLink(transaction.LinkId);
      if (response.success) {
        setTransactions(prev =>
          prev.map(txn =>
            txn.PaymentLinkId === transaction.PaymentLinkId
              ? { ...txn, LinkStatus: 'CANCELLED' }
              : txn
          )
        );
        await refreshProgressJourney();
        return { success: true };
      } else {
        toast.error(response.error || 'Failed to cancel payment link');
        return { success: false };
      }
    } catch (error) {
      console.error('Error cancelling payment link:', error);
      toast.error('Failed to cancel payment link');
      return { success: false };
    }
  }, [leadId]);

  const refreshPaymentLinksSilently = useCallback(async (integrationProductId = null) => {
    if (!leadId) return [];
    try {
      const response = await getLeadPaymentLinks(leadId, integrationProductId);
      if (response.success) {
        setTransactions(response.data || []);
        await refreshProgressJourney();
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error refreshing payment links:', error);
      return [];
    }
  }, [leadId, refreshProgressJourney]);


  return {
    transactions,
    loading,
    syncing,
    transactionSyncing,
    loadPaymentLinks,
    handleSyncAll,
    handleSyncTransaction,
    handleCancelLink,
    refreshPaymentLinksSilently,
    setTransactions,
  };
};
